// Bidirectional mapping between frontend domain types and backend API types.

import type {
  ApiGuest,
  ApiGuestCreate,
  ApiReservation,
  ApiReservationCreate,
  ApiReservationEvent,
  ApiReservationOrigin,
  ApiReservationStatus,
  ApiTable,
  ApiTableCreate,
  ApiZone,
  ApiZoneCreate,
} from "@/types/api";
import type {
  Guest,
  HistoryEntry,
  Reservation,
  ReservationOrigin,
  ReservationStatus,
  Table,
  ZoneConfig,
} from "@/store/types";
import { dayjs, nowInTz, parseInTz } from "@/utils/date";

// ── Status maps ──────────────────────────────────────────────────────────────

const STATUS_TO_API: Record<ReservationStatus, ApiReservationStatus> = {
  reservada: "booked",
  sentado: "seated",
  completada: "completed",
  noshow: "no_show",
  cancelada: "cancelled",
};

const STATUS_FROM_API: Record<ApiReservationStatus, ReservationStatus> = {
  booked: "reservada",
  seated: "sentado",
  completed: "completada",
  no_show: "noshow",
  cancelled: "cancelada",
};

// ── Origin maps ──────────────────────────────────────────────────────────────

const ORIGIN_TO_API: Record<ReservationOrigin, ApiReservationOrigin> = {
  seimseim: "staff",
  walkin: "walk_in",
  google: "google",
  whatsapp: "whatsapp",
  web: "web",
  phone: "phone",
  agent: "agent",
};

const ORIGIN_FROM_API: Record<ApiReservationOrigin, ReservationOrigin> = {
  staff: "seimseim",
  walk_in: "walkin",
  google: "google",
  whatsapp: "whatsapp",
  web: "web",
  phone: "phone",
  agent: "agent",
};

// ── Exported helpers ─────────────────────────────────────────────────────────

export function statusToApi(s: ReservationStatus): ApiReservationStatus {
  return STATUS_TO_API[s];
}

export function statusFromApi(s: ApiReservationStatus): ReservationStatus {
  return STATUS_FROM_API[s];
}

export function originToApi(o: ReservationOrigin): ApiReservationOrigin {
  return ORIGIN_TO_API[o];
}

export function originFromApi(o: ApiReservationOrigin): ReservationOrigin {
  return ORIGIN_FROM_API[o];
}

// ── Zones ────────────────────────────────────────────────────────────────────

export function fromApiZone(z: ApiZone): ZoneConfig {
  const layout = z.layout as Record<string, number>;
  return {
    id: z.id,
    name: z.name,
    color: z.color,
    borderColor: z.border_color,
    x: layout.x ?? 0,
    y: layout.y ?? 0,
    width: layout.width ?? 10,
    height: layout.height ?? 10,
    isBlock: z.is_block,
    isAvailable: z.is_available,
    reservationMode: z.reservation_mode ?? "open",
    publicBookableWeekdays: z.public_bookable_weekdays ?? [0, 1, 2, 3, 4, 5, 6],
  };
}

export function toApiZoneCreate(z: ZoneConfig): ApiZoneCreate {
  return {
    name: z.name,
    color: z.color,
    border_color: z.borderColor,
    layout: { x: z.x, y: z.y, width: z.width, height: z.height },
    is_block: z.isBlock ?? false,
    is_available: z.isAvailable ?? true,
  };
}

export function toApiZoneUpdate(changes: Partial<ZoneConfig>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (changes.name !== undefined) payload.name = changes.name;
  if (changes.color !== undefined) payload.color = changes.color;
  if (changes.borderColor !== undefined) payload.border_color = changes.borderColor;
  if (changes.isBlock !== undefined) payload.is_block = changes.isBlock;
  if (changes.isAvailable !== undefined) payload.is_available = changes.isAvailable;
  if (changes.reservationMode !== undefined) payload.reservation_mode = changes.reservationMode;
  if (changes.publicBookableWeekdays !== undefined)
    payload.public_bookable_weekdays = changes.publicBookableWeekdays;
  if (
    changes.x !== undefined ||
    changes.y !== undefined ||
    changes.width !== undefined ||
    changes.height !== undefined
  ) {
    payload.layout = {
      ...(changes.x !== undefined && { x: changes.x }),
      ...(changes.y !== undefined && { y: changes.y }),
      ...(changes.width !== undefined && { width: changes.width }),
      ...(changes.height !== undefined && { height: changes.height }),
    };
  }
  return payload;
}

// ── Tables ───────────────────────────────────────────────────────────────────

export function fromApiTable(t: ApiTable): Table {
  const layout = t.layout as Record<string, number>;
  return {
    id: t.id,
    name: t.name,
    minCapacity: t.min_capacity,
    maxCapacity: t.max_capacity,
    zoneId: t.zone_id,
    shape: t.shape,
    x: layout.x ?? 0,
    y: layout.y ?? 0,
    neighbors: t.neighbors,
    features: t.features,
    isAvailable: t.is_available,
  };
}

export function toApiTableCreate(t: Table, zoneId?: string): ApiTableCreate {
  return {
    zone_id: zoneId ?? t.zoneId,
    name: t.name,
    min_capacity: t.minCapacity,
    max_capacity: t.maxCapacity,
    shape: t.shape,
    layout: { x: t.x, y: t.y },
    neighbors: t.neighbors,
    features: t.features,
    is_available: t.isAvailable ?? true,
  };
}

export function toApiTableUpdate(changes: Partial<Table>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (changes.name !== undefined) payload.name = changes.name;
  if (changes.minCapacity !== undefined) payload.min_capacity = changes.minCapacity;
  if (changes.maxCapacity !== undefined) payload.max_capacity = changes.maxCapacity;
  if (changes.shape !== undefined) payload.shape = changes.shape;
  if (changes.zoneId !== undefined) payload.zone_id = changes.zoneId;
  if (changes.neighbors !== undefined) payload.neighbors = changes.neighbors;
  if (changes.features !== undefined) payload.features = changes.features;
  if (changes.isAvailable !== undefined) payload.is_available = changes.isAvailable;
  if (changes.x !== undefined || changes.y !== undefined) {
    payload.layout = {
      ...(changes.x !== undefined && { x: changes.x }),
      ...(changes.y !== undefined && { y: changes.y }),
    };
  }
  return payload;
}

// ── Guests ───────────────────────────────────────────────────────────────────

export function fromApiGuest(g: ApiGuest): Guest {
  return {
    id: g.id,
    name: g.name,
    email: g.email,
    phone: g.phone,
    notes: g.notes,
    rating: g.rating ?? 0,
    isVip: g.is_vip,
    totalVisits: g.completed_count,
    // Other stats are not yet exposed by the API — zeroed out.
    clientSince: "",
    favoriteTable: "",
    attended: 0,
    noShows: 0,
    cancellations: 0,
    avgPartySize: 0,
  };
}

export function toApiGuestCreate(g: Partial<Guest>): ApiGuestCreate {
  return {
    name: g.name ?? "",
    ...(g.phone && { phone: g.phone }),
    ...(g.email && { email: g.email }),
    ...(g.notes && { notes: g.notes }),
    ...(g.rating && { rating: g.rating }),
    ...(g.isVip !== undefined && { is_vip: g.isVip }),
  };
}

/** PATCH ``/api/guests/{id}/`` — only includes fields the caller actually
 *  wants to change. ``rating: 0`` is sent as ``null`` so the backend can
 *  clear the value when the user removes the rating. */
export function toApiGuestUpdate(g: Partial<Guest>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (g.name !== undefined) payload.name = g.name;
  if (g.phone !== undefined) payload.phone = g.phone;
  if (g.email !== undefined) payload.email = g.email;
  if (g.notes !== undefined) payload.notes = g.notes;
  if (g.rating !== undefined) payload.rating = g.rating === 0 ? null : g.rating;
  if (g.isVip !== undefined) payload.is_vip = g.isVip;
  return payload;
}

// ── Reservations ─────────────────────────────────────────────────────────────

export function fromApiReservation(r: ApiReservation, locationTz?: string): Reservation {
  const startsAt = locationTz ? dayjs(r.starts_at).tz(locationTz) : dayjs(r.starts_at);
  const endsAt = locationTz ? dayjs(r.ends_at).tz(locationTz) : dayjs(r.ends_at);

  return {
    id: r.id,
    guestId: r.guest_id ?? null,
    guestName: r.guest_name,
    guestPhone: r.guest_phone,
    guestEmail: r.guest_email,
    guestIsRisk: r.guest_is_risk ?? false,
    pax: r.pax,
    date: startsAt.format("YYYY-MM-DD"),
    time: startsAt.format("h:mm A"),
    endTime: endsAt.format("h:mm A"),
    // Distinct from ``date`` when the reservation crosses local midnight
    // (e.g. 22:00 → 01:00). Consumers that parse ``${date} ${endTime}``
    // to a datetime would land the end before the start in that case;
    // ``endDate`` is the load-bearing companion.
    endDate: endsAt.format("YYYY-MM-DD"),
    tableIds: r.tables,
    status: statusFromApi(r.status),
    origin: originFromApi(r.origin),
    preferredZone: r.preferred_zone_id ?? "",
    notes: r.notes,
    internalNotes: r.internal_notes,
    tags: r.tags,
    features: r.features,
    occasion: r.occasion,
    isVip: r.is_vip,
    isConfirmedByHost: r.is_confirmed,
    seatedAt: r.seated_at ?? "",
    cancelledAt: r.cancelled_at ?? null,
    pinLevel: r.pin_level,
    delayMinutes: r.delay_minutes ?? 0,
    confirmationToken: r.confirmation_token,
    manageUrl: r.manage_url ?? "",
    originWaitlistEntryId: r.origin_waitlist_entry_id ?? undefined,
    createdAt: dayjs(r.created_at).valueOf(),
    history: [],
  };
}

// TODO: unused — no callers in the frontend (verified 2026-05-20).
// Drops `location_id`, which is required by `ApiReservationCreate`, so tsc
// already flags this function. Candidate for removal alongside any cleanup
// of the legacy create flow. Left in place because it's outside the scope
// of the detail-panel redesign that touched this file.
export function toApiReservationCreate(
  r: Partial<Reservation>,
  locationTz?: string,
): ApiReservationCreate {
  const date = r.date ?? nowInTz(locationTz).format("YYYY-MM-DD");
  const startsAt = parseInTz(`${date} ${r.time}`, "YYYY-MM-DD h:mm A", locationTz);
  const endsAt = r.endTime
    ? parseInTz(`${date} ${r.endTime}`, "YYYY-MM-DD h:mm A", locationTz)
    : startsAt.add(3, "hour");

  return {
    guest_name: r.guestName ?? "",
    pax: r.pax ?? 1,
    starts_at: startsAt.toISOString(),
    ends_at: endsAt.toISOString(),
    ...(r.tableIds?.length && { tables: r.tableIds }),
    ...(r.preferredZone && { preferred_zone_id: r.preferredZone }),
    ...(r.origin && { origin: originToApi(r.origin) }),
    ...(r.status && { status: statusToApi(r.status) }),
    ...(r.internalNotes && { internal_notes: r.internalNotes }),
    ...(r.occasion &&
      r.occasion !== "none" && { occasion: r.occasion as ApiReservationCreate["occasion"] }),
    ...(r.tags?.length && { tags: r.tags as ApiReservationCreate["tags"] }),
    ...(r.features?.length && { features: r.features as ApiReservationCreate["features"] }),
    ...(r.isVip !== undefined && { is_vip: r.isVip }),
  };
}

export function fromApiReservationEvent(e: ApiReservationEvent): HistoryEntry {
  return {
    timestamp: dayjs(e.created_at).valueOf(),
    action: e.event_type,
    detail: e.detail,
    actorName: e.actor_name || undefined,
    causedByLabel: e.caused_by_label || undefined,
  };
}
